import { stateAbbreviations, locationNames } from '../constants/locations';
import { getCrimeTypes } from '../constants/crimes';

const DEFAULTS = {
  crime: 'all_crime',
  year: '2024',
  source: 'FBI_CIUS',
  tab: 'reports',
  view: 'table',
};

/** Internal source id → lowercase query value (best-practice stable URLs). */
const SOURCE_TO_PARAM = {
  FBI_CIUS: 'fbi_cius',
  BJS_NCVS: 'bjs_ncvs',
};

const PARAM_TO_SOURCE = Object.fromEntries(
  Object.entries(SOURCE_TO_PARAM).map(([k, v]) => [v, k])
);

/** Internal view → URL query value (table=data, chart=charts). */
const VIEW_TO_QUERY = { table: 'data', chart: 'charts', map: 'map' };
const QUERY_TO_VIEW = { data: 'table', charts: 'chart', map: 'map' };

const APP_STATE_SEGMENT_COUNT = 5;

const ALLOWED_TABS = new Set(['reports', 'totals', 'trends', 'rates', 'scores']);
const ALLOWED_VIEWS = new Set(['table', 'chart', 'map']);
const ALLOWED_COMPARE_SCOPES = new Set(['state', 'national']);
const ALLOWED_RATE_TYPES = new Set(['county', 'city', 'metro', 'state', 'national']);

const ALLOWED_SORT_DIRS = new Set(['asc', 'desc']);
const MAX_TABLE_SORT_LEN = 192;
const ALLOWED_MAP_RANGES = new Set([
  'count', 'count_avg', 'avg_diff', 'avg_diff_pct', 'count_vs_avg',
  'prev_count', 'trend_diff', 'trend_diff_pct', 'count_vs_prev',
  'rate', 'national_rate', 'rate_diff', 'rate_diff_pct', 'rate_vs_national_rate',
  'composite_score', 'composite_score_rank', 'score',
  'rank', 'percentile',
]);
const ALLOWED_POP_GROUPS = new Set([
  'all', 'under10k', 'under100k',
  '10kplus', '25kplus', '50kplus', '100kplus', '250kplus', '500kplus',
  '1mplus', '2_5mplus', '5mplus', '10mplus',
]);

/** Sort column key for LocationType aggregate tables (query param `table_sort`). */
export function normalizeTableSortKey(raw) {
  if (raw == null || raw === '') return null;
  const s = String(raw);
  if (s.length > MAX_TABLE_SORT_LEN) return null;
  if (!/^[a-z0-9_]+$/i.test(s)) return null;
  return s;
}

export function normalizeSortDir(raw) {
  const s = String(raw || '').toLowerCase();
  return ALLOWED_SORT_DIRS.has(s) ? s : null;
}

/**
 * True for aggregate paths like /cities/all, /states/all, "All OH Cities", "All States", etc.
 */
export function isLocationTypeAggregateLocation(location) {
  const n = location?.name;
  const t = location?.type;
  if (typeof n !== 'string' || !n.startsWith('All ')) return false;
  return t === 'county' || t === 'city' || t === 'metro' || t === 'state' || t === 'region' || t === 'division';
}

function normalizeTab(t) {
  const s = String(t || '').toLowerCase();
  return ALLOWED_TABS.has(s) ? s : DEFAULTS.tab;
}

function normalizeView(v) {
  const s = String(v || '').toLowerCase();
  return ALLOWED_VIEWS.has(s) ? s : DEFAULTS.view;
}

function normalizeViewFromQuery(v) {
  const s = String(v || '').toLowerCase();
  if (QUERY_TO_VIEW[s]) return QUERY_TO_VIEW[s];
  return normalizeView(s);
}

const safeDecode = (s) => {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
};

/** Query or path segment: `data` | `charts` | `map` | internal `table` | `chart` | `map`. */
function isValidGuestViewToken(raw) {
  const s = String(raw).toLowerCase();
  return ALLOWED_VIEWS.has(s) || QUERY_TO_VIEW[s] != null;
}

function validateGuestQueryTabView(params) {
  const rawTab = params.get('analysis_tab') || params.get('tab');
  if (rawTab != null && rawTab !== '') {
    if (!ALLOWED_TABS.has(String(rawTab).toLowerCase())) return false;
  }
  const rawView = params.get('view');
  if (rawView != null && rawView !== '') {
    if (!isValidGuestViewToken(rawView)) return false;
  }
  const rawTs = params.get('table_sort');
  if (rawTs != null && rawTs !== '' && normalizeTableSortKey(rawTs) == null) {
    return false;
  }
  const rawSd = params.get('sort_dir');
  if (rawSd != null && rawSd !== '' && normalizeSortDir(rawSd) == null) {
    return false;
  }
  const rawChart = params.get('chart');
  if (rawChart != null && rawChart !== '' && !ALLOWED_MAP_RANGES.has(String(rawChart).toLowerCase())) {
    return false;
  }
  const rawPg = params.get('pop_group');
  if (rawPg != null && rawPg !== '' && !ALLOWED_POP_GROUPS.has(String(rawPg).toLowerCase())) {
    return false;
  }
  return true;
}

/** Legacy path-encoded app state: last five segments include tab + view. */
function validateGuestPathTabView(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  const n = segments.length;
  if (n !== 7 && n !== 8) return true;
  const app = segments.slice(-APP_STATE_SEGMENT_COUNT);
  const rawTab = safeDecode(app[3]);
  const rawView = safeDecode(app[4]);
  if (rawTab && !ALLOWED_TABS.has(String(rawTab).toLowerCase())) return false;
  if (rawView && !isValidGuestViewToken(rawView)) return false;
  return true;
}

const stateAbbrToFull = {};
Object.entries(stateAbbreviations).forEach(([full, abbr]) => {
  stateAbbrToFull[abbr.toLowerCase()] = full;
});

const typeToSegment = {
  nation: 'nation',
  region: 'regions',
  division: 'divisions',
  state: 'states',
  metro: 'metros',
  county: 'counties',
  city: 'cities',
};

const segmentToType = {};
Object.entries(typeToSegment).forEach(([type, segment]) => {
  segmentToType[segment] = type;
});

/**
 * Location URL segments: comma-space (and comma) → `_`, spaces → `_`, hyphens preserved.
 */
function toLocationSlug(str) {
  return str
    .toLowerCase()
    .replace(/,\s*/g, '_')
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_-]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

/** Legacy hyphen slugs (old bookmarks / shared links). */
function toLegacyHyphenSlug(str) {
  return str
    .toLowerCase()
    .replace(/,\s*/g, '-')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function slugMatches(str, urlSlug) {
  return toLocationSlug(str) === urlSlug || toLegacyHyphenSlug(str) === urlSlug;
}

function findStateBySlug(slug) {
  return locationNames.State.find((s) => slugMatches(s, slug)) || null;
}

/**
 * Build a URL path from location info ({ name, type }).
 * Returns '/' when the input can't be mapped.
 */
export function locationToPath(locationInfo) {
  if (!locationInfo?.name || !locationInfo?.type) return '/';

  const { name, type } = locationInfo;
  const segment = typeToSegment[type];
  if (!segment) return '/';

  // "All X" aggregate views
  if (name.startsWith('All ')) {
    const match = name.match(
      /^All\s+(?:([A-Z]{2})\s+)?(Counties|Cities|Metros|States|Regions|Divisions)$/
    );
    if (match) {
      const stateAbbr = match[1];

      // State-specific aggregates for counties/cities use the state path segment
      if (stateAbbr && (type === 'county' || type === 'city')) {
        const stateFull = stateAbbrToFull[stateAbbr.toLowerCase()];
        if (stateFull) return `/${segment}/${toLocationSlug(stateFull)}/all`;
      }

      return `/${segment}/all`;
    }
    return '/';
  }

  switch (type) {
    case 'nation':
      return `/nation/${toLocationSlug('United States')}`;

    case 'region':
    case 'division':
    case 'state':
      return `/${segment}/${toLocationSlug(name)}`;

    case 'metro': {
      const metroName = name.replace(/\s+Metro$/, '');
      return `/${segment}/${toLocationSlug(metroName)}_metro`;
    }

    case 'county': {
      const commaIdx = name.lastIndexOf(',');
      if (commaIdx !== -1) {
        const countyPart = toLocationSlug(name.slice(0, commaIdx).trim());
        const stateName = name.slice(commaIdx + 1).trim();
        return `/${segment}/${toLocationSlug(stateName)}/${countyPart}`;
      }
      return `/${segment}/${toLocationSlug(name)}`;
    }

    case 'city': {
      const parts = name.split(',').map((p) => p.trim());
      if (parts.length >= 2) {
        const stateName = parts[parts.length - 1];
        const cityPart = toLocationSlug(parts.slice(0, -1).join(' '));
        return `/${segment}/${toLocationSlug(stateName)}/${cityPart}`;
      }
      return `/${segment}/${toLocationSlug(name)}`;
    }

    default:
      return '/';
  }
}

/**
 * Build query string for app state (canonical names).
 * Always includes all five params so the URL fully reflects state (shareable deep links).
 * Order: crime_type → date_range → data_source → analysis_tab → view → optional historical/compare/rate.
 * e.g. ?crime_type=all_crime&date_range=2023&data_source=fbi_cius&analysis_tab=totals&view=data
 * Optional: table_sort + sort_dir for LocationType aggregate table sorting (back/forward).
 */
export function buildAppSearch({
  crime, year, source, tab, view,
  historical,
  compareScope,
  rateType,
  tableSort,
  sortDir,
  chart,
  popGroup,
  page,
  perPage,
} = {}) {
  const c = crime ?? DEFAULTS.crime;
  const y = year ?? DEFAULTS.year;
  const src = source ?? DEFAULTS.source;
  const t = tab ?? DEFAULTS.tab;
  const v = view ?? DEFAULTS.view;
  const params = new URLSearchParams();
  params.set('crime_type', c);
  params.set('date_range', String(y));
  params.set('data_source', SOURCE_TO_PARAM[src] || String(src).toLowerCase());
  params.set('analysis_tab', t);
  params.set('view', VIEW_TO_QUERY[v] || v);
  if (historical) params.set('historical', 'yes');
  if (compareScope && ALLOWED_COMPARE_SCOPES.has(String(compareScope).toLowerCase())) {
    params.set('compare_scope', String(compareScope).toLowerCase());
  }
  if (rateType && ALLOWED_RATE_TYPES.has(String(rateType).toLowerCase())) {
    params.set('rate_type', String(rateType).toLowerCase());
  }
  const ts = normalizeTableSortKey(tableSort);
  const sd = normalizeSortDir(sortDir);
  if (ts) {
    params.set('table_sort', ts);
    params.set('sort_dir', sd || 'desc');
  }
  if (chart && ALLOWED_MAP_RANGES.has(chart)) {
    params.set('chart', chart);
  }
  if (popGroup && popGroup !== 'all') {
    params.set('pop_group', popGroup);
  }
  if (page && page > 1) {
    params.set('page', String(page));
  }
  if (perPage && perPage !== 20) {
    params.set('per_page', String(perPage));
  }
  return `?${params.toString()}`;
}

/**
 * Parse query string → internal app state. Supports legacy ?crime=&year=&source=&tab=&view=.
 */
export function parseAppQuery(search) {
  const params = new URLSearchParams(search);
  const crime =
    params.get('crime_type') || params.get('crime') || DEFAULTS.crime;
  const year =
    params.get('date_range') || params.get('year') || DEFAULTS.year;
  const rawSource = params.get('data_source') || params.get('source') || '';
  const source = rawSource
    ? PARAM_TO_SOURCE[String(rawSource).toLowerCase()] || DEFAULTS.source
    : DEFAULTS.source;
  const tab = normalizeTab(
    params.get('analysis_tab') || params.get('tab')
  );
  const viewParam = params.get('view');
  const view =
    viewParam != null && viewParam !== ''
      ? normalizeViewFromQuery(viewParam)
      : DEFAULTS.view;

  const rawHist = params.get('historical');
  const h = String(rawHist || '').toLowerCase();
  const historical = h === 'yes' || h === 'true' || h === '1';

  const rawCs = params.get('compare_scope');
  const compareScope =
    rawCs && ALLOWED_COMPARE_SCOPES.has(String(rawCs).toLowerCase())
      ? String(rawCs).toLowerCase()
      : null;

  const rawRt = params.get('rate_type');
  const rateType =
    rawRt && ALLOWED_RATE_TYPES.has(String(rawRt).toLowerCase())
      ? String(rawRt).toLowerCase()
      : null;

  const tableSort = normalizeTableSortKey(params.get('table_sort'));
  const rawSortDir = params.get('sort_dir');
  const sortDir =
    tableSort && rawSortDir != null && rawSortDir !== ''
      ? normalizeSortDir(rawSortDir) || 'desc'
      : tableSort
        ? 'desc'
        : null;

  const rawChart = params.get('chart');
  const chart =
    rawChart && ALLOWED_MAP_RANGES.has(String(rawChart).toLowerCase())
      ? String(rawChart).toLowerCase()
      : null;

  const rawPopGroup = params.get('pop_group');
  const popGroup =
    rawPopGroup && ALLOWED_POP_GROUPS.has(String(rawPopGroup).toLowerCase())
      ? String(rawPopGroup).toLowerCase()
      : null;

  const rawPage = params.get('page');
  const page = rawPage && /^\d+$/.test(rawPage) && parseInt(rawPage, 10) > 0
    ? parseInt(rawPage, 10)
    : null;

  const rawPerPage = params.get('per_page');
  const perPage = rawPerPage && /^\d+$/.test(rawPerPage) && parseInt(rawPerPage, 10) > 0
    ? parseInt(rawPerPage, 10)
    : null;

  return {
    crime,
    year,
    source,
    tab,
    view,
    historical,
    compareScope,
    rateType,
    tableSort,
    sortDir,
    chart,
    popGroup,
    page,
    perPage,
  };
}

const TABLE_SORT_QUERY_KEYS = new Set(['table_sort', 'sort_dir']);

/**
 * True when pathname and all query params except table_sort / sort_dir match, and sort
 * params differ. Used to replace the last history entry when the URL sync effect fires
 * twice for one logical step (e.g. crime updated then table_sort catches up).
 */
export function urlsMatchExceptTableSort(prevPathAndSearch, nextPathAndSearch) {
  if (!prevPathAndSearch || !nextPathAndSearch || prevPathAndSearch === nextPathAndSearch) {
    return false;
  }
  const base = 'http://krime.local';
  let a;
  let b;
  try {
    a = new URL(prevPathAndSearch, base);
    b = new URL(nextPathAndSearch, base);
  } catch {
    return false;
  }
  if (a.pathname !== b.pathname) return false;
  const keys = new Set([...a.searchParams.keys(), ...b.searchParams.keys()]);
  for (const k of keys) {
    if (TABLE_SORT_QUERY_KEYS.has(k)) continue;
    const va = a.searchParams.get(k) ?? '';
    const vb = b.searchParams.get(k) ?? '';
    if (va !== vb) return false;
  }
  const sortA = `${a.searchParams.get('table_sort') ?? ''}|${a.searchParams.get('sort_dir') ?? ''}`;
  const sortB = `${b.searchParams.get('table_sort') ?? ''}|${b.searchParams.get('sort_dir') ?? ''}`;
  return sortA !== sortB;
}

/**
 * Serialize internal rate view mode for the URL. Metro "National" compare uses
 * { rateType: 'state', rateScope: 'national' } internally; we emit rate_type=national
 * so the link matches the UI label (legacy: rate_type=state&compare_scope=national still parses).
 */
export function encodeRateTypeForUrl(locationType, rateType, rateScope) {
  const lt = String(locationType || '').toLowerCase();
  if (lt === 'metro' && rateType === 'state' && rateScope === 'national') {
    return 'national';
  }
  return rateType;
}

/**
 * For city/county locations, encode both the scope (state|national) and the rate comparison
 * type into URL params.  Peer rates (city|county) get rate_type=city|county; aggregate rates
 * (all_cities|all_counties) get rate_type matching the scope (state|national).
 *
 * This lets the URL distinguish all four rate modes:
 *   OH City  → rate_type=city   & compare_scope=state
 *   OH State → rate_type=state  & compare_scope=state
 *   US City  → rate_type=city   & compare_scope=national
 *   US Natl  → rate_type=national & compare_scope=national
 */
export function encodeCountyCityCompareUrl(locationType, rateType, rateScope) {
  const lt = String(locationType || '').toLowerCase();
  if (lt !== 'city' && lt !== 'county') return null;

  const rs = rateScope === 'national' ? 'national' : 'state';
  const isPeer = rateType === 'city' || rateType === 'county';
  return { compareScope: rs, rateType: isPeer ? lt : rs };
}

/** Prefer compare_scope when rate_type disagrees (legacy links). */
function resolveCountyCityScopeToken(rateType, compareScope) {
  const v = (x) => {
    const s = String(x || '').toLowerCase();
    if (s === 'state' || s === 'national') return s;
    return null;
  };
  const r = v(rateType);
  const c = v(compareScope);
  if (c && r && c !== r) return c;
  return c || r || null;
}

/**
 * Map URL rate params to internal rateViewMode.
 *
 * City/county:
 *   rate_type=city|county  → peer comparison  (rateType = city|county)
 *   rate_type=state|national → aggregate comparison (rateType = all_cities|all_counties)
 */
export function decodeRateViewModeFromUrl({
  locationType,
  rateType,
  compareScope,
  locationName,
} = {}) {
  const lt = String(locationType || '').toLowerCase();

  if (lt === 'metro') {
    if (!rateType) return null;
    const cs = compareScope === 'national' ? 'national' : 'state';
    if (rateType === 'metro') return { rateType: 'metro', rateScope: 'national' };
    if (rateType === 'national') return { rateType: 'state', rateScope: 'national' };
    if (rateType === 'state' && cs === 'national') {
      return { rateType: 'state', rateScope: 'national' };
    }
    return { rateType, rateScope: cs };
  }

  if (lt === 'city' || lt === 'county') {
    const cs = compareScope === 'national' ? 'national' : 'state';

    if (rateType === 'city' || rateType === 'county') {
      return { rateType: lt, rateScope: cs };
    }

    const resolved = resolveCountyCityScopeToken(rateType, compareScope);
    if (resolved == null) return null;

    const aggregate = lt === 'county' ? 'all_counties' : 'all_cities';
    return { rateType: aggregate, rateScope: resolved };
  }

  if (!rateType) return null;
  if (rateType === 'national') {
    return { rateType: 'national', rateScope: 'national' };
  }
  const cs = compareScope === 'national' ? 'national' : 'state';
  return { rateType, rateScope: cs };
}

/** Inclusive calendar-year bounds aligned with filter dropdowns (Filters.jsx). */
const YEAR_RANGE_BY_SOURCE = {
  FBI_CIUS: [1999, 2024],
  BJS_NCVS: [1994, 2024],
};

function collectValidCrimeValuesForSource(source) {
  const set = new Set();
  for (const t of getCrimeTypes(source)) {
    set.add(t.value);
    if (t.subTypes) {
      for (const s of t.subTypes) set.add(s.value);
    }
  }
  return set;
}

/**
 * Normalize source from query or legacy path segments to FBI_CIUS | BJS_NCVS, or null if unusable.
 */
function normalizeInternalSource(source) {
  if (source == null || source === '') return DEFAULTS.source;
  const str = String(source);
  if (SOURCE_TO_PARAM[str]) return str;
  const fromParam = PARAM_TO_SOURCE[str.toLowerCase()];
  if (fromParam) return fromParam;
  return null;
}

/**
 * Logged-out deep links: only allow crime/year/source/tab/view combos that the app can load.
 * Validates query params; if `data_source` is present it must be a known token.
 * Uses full pathname + search so legacy path-encoded app segments are checked too.
 */
export function isGuestDeepLinkAppStateValid(pathname, search = '') {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search
  );
  if (!validateGuestQueryTabView(params)) return false;
  if (!validateGuestPathTabView(pathname)) return false;

  const rawSource = params.get('data_source') || params.get('source') || '';
  if (rawSource && !PARAM_TO_SOURCE[String(rawSource).toLowerCase()]) {
    return false;
  }

  const parsed = parseKrimePathname(pathname, search);
  const sourceNorm = normalizeInternalSource(parsed.source);
  if (sourceNorm == null) return false;

  const yearStr = String(parsed.year ?? '');
  if (!/^\d{4}$/.test(yearStr)) return false;
  const y = parseInt(yearStr, 10);
  const range = YEAR_RANGE_BY_SOURCE[sourceNorm];
  if (!range || y < range[0] || y > range[1]) return false;

  const validCrimes = collectValidCrimeValuesForSource(sourceNorm);
  if (!validCrimes.has(parsed.crime)) return false;

  return true;
}

/** @deprecated Use buildAppSearch — kept for older links using crime/year/source */
export function filtersToSearch({ crime, year, source } = {}) {
  return buildAppSearch({ crime, year, source, tab: DEFAULTS.tab, view: DEFAULTS.view });
}

/** @deprecated Use parseAppQuery */
export function searchToFilters(search) {
  return parseAppQuery(search);
}

/**
 * Location path + app query (?crime_type=…&date_range=…&data_source=…&analysis_tab=…&view=…).
 */
export function buildKrimeUrl(
  locationInfo,
  {
    crime, year, source, tab, view,
    historical, compareScope, rateType,
    tableSort, sortDir,
    chart, popGroup,
    page, perPage,
  } = {}
) {
  const base = locationToPath(locationInfo);
  if (base === '/') return '/';
  const search = buildAppSearch({
    crime, year, source, tab, view,
    historical, compareScope, rateType,
    tableSort, sortDir,
    chart, popGroup,
    page, perPage,
  });
  return `${base}${search}`;
}

/**
 * Parse pathname + search into location and app state.
 * Query params are canonical; legacy path suffixes (7/8 segments) still supported.
 */
export function parseKrimePathname(pathname, search = '') {
  const segments = pathname.split('/').filter(Boolean);
  const n = segments.length;
  const queryState = parseAppQuery(search);

  const fromAppSegments = (app) => ({
    crime: safeDecode(app[0]) || queryState.crime,
    year: safeDecode(app[1]) || queryState.year,
    source: safeDecode(app[2]) || queryState.source,
    tab: normalizeTab(safeDecode(app[3]) || queryState.tab),
    view: normalizeView(safeDecode(app[4]) || queryState.view),
  });

  if (n === 3 + APP_STATE_SEGMENT_COUNT) {
    const locPath = `/${segments.slice(0, 3).join('/')}`;
    const loc = pathToLocation(locPath);
    if (loc) {
      const app = segments.slice(-APP_STATE_SEGMENT_COUNT);
      return { location: loc, ...queryState, ...fromAppSegments(app) };
    }
  }
  if (n === 2 + APP_STATE_SEGMENT_COUNT) {
    const locPath = `/${segments.slice(0, 2).join('/')}`;
    const loc = pathToLocation(locPath);
    if (loc) {
      const app = segments.slice(-APP_STATE_SEGMENT_COUNT);
      return { location: loc, ...queryState, ...fromAppSegments(app) };
    }
  }

  const loc = pathToLocation(pathname);
  return {
    location: loc,
    ...queryState,
  };
}

/**
 * Build a full absolute URL for sharing.
 */
export function buildShareUrl(
  locationInfo,
  {
    crime, year, source, tab, view,
    historical, compareScope, rateType,
    tableSort, sortDir,
    chart, popGroup,
  } = {}
) {
  const path = buildKrimeUrl(locationInfo, {
    crime, year, source, tab, view,
    historical, compareScope, rateType,
    tableSort, sortDir,
    chart, popGroup,
  });
  if (path === '/') return window.location.origin;
  return `${window.location.origin}${path}`;
}

/**
 * Human-readable data source for export footers (FBI CIUS vs BJS NCVS).
 */
export function getExportDataSourceDisplayName(selectedSource) {
  switch (selectedSource) {
    case 'FBI_CIUS':
      return 'FBI (CIUS)';
    case 'BJS_NCVS':
      return 'BJS (NCVS)';
    default:
      return selectedSource ? String(selectedSource).replace(/_/g, ' ') : 'FBI (CIUS)';
  }
}

/**
 * Canonical URL for CSV/TXT/XLSX/PDF source footers. Mirrors in-app navigation (crime, date,
 * source, tab, view, compare_scope, rate_type). Reports tab never includes historical=yes
 * (report cards are current-year summaries). Report cards include compare_scope and rate_type
 * when county/city/metro so the link matches the comparison controls shown on the cards.
 */
export function buildExportSourceUrl(ctx) {
  if (typeof window === 'undefined') return '';
  const {
    locationData,
    selectedCrime,
    selectedDateRange,
    selectedSource,
    activeTab,
    selectedView,
    isHistorical,
    compareMode,
    rateViewMode,
  } = ctx || {};

  const info = locationData?.info;
  if (!info?.type) {
    return window.location.href;
  }

  const locType = info.type.toLowerCase();
  const isCountyOrCity = locType === 'county' || locType === 'city';
  const isMetro = locType === 'metro';

  const effectiveHistorical = activeTab === 'reports' ? false : !!isHistorical;

  let compareScope;
  let rateType;

  const countyCityPair = isCountyOrCity && activeTab === 'rates'
    ? encodeCountyCityCompareUrl(
        locType,
        rateViewMode?.rateType,
        rateViewMode?.rateScope
      )
    : null;

  if (countyCityPair) {
    compareScope = countyCityPair.compareScope;
    rateType = countyCityPair.rateType;
  } else if (activeTab === 'rates' && isMetro) {
    compareScope = rateViewMode?.rateScope;
    rateType = encodeRateTypeForUrl(
      locType,
      rateViewMode?.rateType,
      rateViewMode?.rateScope
    );
  } else if (activeTab === 'reports' && isMetro) {
    compareScope = rateViewMode?.rateScope;
    rateType = encodeRateTypeForUrl(
      locType,
      rateViewMode?.rateType,
      rateViewMode?.rateScope
    );
  } else if (isCountyOrCity) {
    compareScope = compareMode;
  }

  return buildShareUrl(info, {
    crime: selectedCrime,
    year: selectedDateRange,
    source: selectedSource,
    tab: activeTab,
    view: selectedView,
    historical: effectiveHistorical,
    compareScope,
    rateType,
  });
}

/**
 * Parse a URL pathname into { name, type } or null.
 */
export function pathToLocation(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length < 2) return null;

  const urlSegment = segments[0];
  const type = segmentToType[urlSegment];
  if (!type) return null;

  const slug = decodeURIComponent(segments[1]);

  // --- 2-segment national aggregates: /counties/all, /cities/all, /metros/all, etc. ---
  if (segments.length === 2 && slug === 'all') {
    const pluralMap = {
      county: 'Counties',
      city: 'Cities',
      metro: 'Metros',
      state: 'States',
      region: 'Regions',
      division: 'Divisions',
    };
    const plural = pluralMap[type];
    return plural ? { name: `All ${plural}`, type } : null;
  }

  // --- 3-segment paths for counties & cities: /counties/{state}/{slug} ---
  if (segments.length >= 3 && (type === 'county' || type === 'city')) {
    const stateSlug = decodeURIComponent(segments[1]);
    const locationSlug = decodeURIComponent(segments[2]);
    const stateFull = findStateBySlug(stateSlug);
    if (!stateFull) return null;

    // State-specific aggregate: /counties/ohio/all → All OH Counties
    if (locationSlug === 'all') {
      const stateAbbr = stateAbbreviations[stateFull];
      const plural = type === 'county' ? 'Counties' : 'Cities';
      return {
        name: `All ${stateAbbr} ${plural}`,
        type,
        state: stateFull,
        stateAbbr,
      };
    }

    if (type === 'county') {
      const match = locationNames.County.find((c) => {
        const commaIdx = c.lastIndexOf(',');
        if (commaIdx === -1) return false;
        const countyName = c.slice(0, commaIdx).trim();
        const state = c.slice(commaIdx + 1).trim();
        return slugMatches(countyName, locationSlug) && state === stateFull;
      });
      return match
        ? {
            name: match,
            type: 'county',
            state: stateFull,
            stateAbbr: stateAbbreviations[stateFull],
          }
        : null;
    }

    const match = locationNames.City.find((c) => {
      const parts = c.split(',').map((p) => p.trim());
      const cityName = parts.slice(0, -1).join(' ');
      const state = parts[parts.length - 1];
      return slugMatches(cityName, locationSlug) && state === stateFull;
    });
    return match
      ? {
          name: match,
          type: 'city',
          state: stateFull,
          stateAbbr: stateAbbreviations[stateFull],
        }
      : null;
  }

  // --- 2-segment paths for everything else ---
  switch (type) {
    case 'nation':
      return { name: 'United States', type: 'nation' };

    case 'region': {
      const match = locationNames.Region.find((r) => slugMatches(r, slug));
      return match ? { name: match, type: 'region' } : null;
    }

    case 'division': {
      const match = locationNames.Division.find((d) => slugMatches(d, slug));
      return match ? { name: match, type: 'division' } : null;
    }

    case 'state': {
      const match = locationNames.State.find((s) => slugMatches(s, slug));
      return match ? { name: match, type: 'state' } : null;
    }

    case 'metro': {
      const match = locationNames.Metro.find((m) => {
        const metroName = m.replace(/\s+Metro$/, '');
        if (`${toLocationSlug(metroName)}_metro` === slug) return true;

        const commaIdx = metroName.lastIndexOf(',');
        if (commaIdx !== -1) {
          const localName = metroName.slice(0, commaIdx).trim();
          const statePart = metroName
            .slice(commaIdx + 1)
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '');
          const legacyPlus = `${toLegacyHyphenSlug(localName)}+${statePart}-metro`;
          const legacyPlusUnderscore = `${toLocationSlug(localName)}+${statePart}-metro`;
          if (slug === legacyPlus || slug === legacyPlusUnderscore) return true;
        }
        return (
          `${toLegacyHyphenSlug(metroName)}-metro` === slug ||
          `${toLocationSlug(metroName)}-metro` === slug
        );
      });
      return match ? { name: match, type: 'metro' } : null;
    }

    default:
      return null;
  }
}
